/**
 * Epic 12 — export do statické HTML dokumentace (E12-US1 až US4).
 *
 * Čistá funkce bez závislosti na Reactu/DOM (`buildExportHtml`) — sestaví
 * jeden self-contained `.html` řetězec: horní část je statický vizuální
 * snímek canvasu jako **inline SVG** (epic-12 kap. 8 doporučení), dolní část
 * je dokumentační sekce s plným detailem každé tabulky. Proklik z diagramu na
 * dokumentaci (E12-US4) jde čistě přes HTML kotvy (`<a href="#table-doc-…">`),
 * žádný vlastní JS.
 *
 * Diagram záměrně NEPOČÍTÁ pozice/velikosti/skupiny znovu — přebírá je
 * hotové od volajícího (`ExportPanel.tsx`, stejné `effectiveTablePositions`/
 * `tableSizes`, jaké má právě vykreslené `CanvasView.tsx`), aby export
 * vypadal PŘESNĚ jako aktuální stav editoru (E12-US2 AC), ne jako nově
 * přepočítaný layout. Geometrie FK hrany (`computeEdgeVisual`) je stejná
 * funkce, jakou používá živé vykreslení v `ForeignKeyEdge.tsx` — jedna
 * sdílená implementace, ne dvě, co by se mohly rozejít.
 *
 * Export nikdy nečte `ProjectFile.connection` (citlivé údaje o připojení se
 * sem vůbec nedostanou, E12-US1 AC) a nevolá žádné API — sestavení proběhne
 * čistě na klientovi z dat, která editor už má v paměti.
 */
import type {
  CanvasMetadata,
  ColorToken,
  ColumnModel,
  Group,
  Note,
  SchemaModel,
  TableModel,
  ViewSettings,
} from '@bits2schema/shared';
import { getEdgeRoute, resolveForeignKeyEdges } from '@bits2schema/shared';
import {
  NOTE_STACK_OFFSET,
  TABLE_NODE_WIDTH,
  columnRowCenterOffset,
  computeEdgeVisual,
  computeGroupBoundingBox,
  visibleColumns,
  type BoundingBox,
  type BoxSize,
  type EdgeEndpoint,
  type Point,
} from '../canvas/geometry';
import { colorTokenBackgroundVar, colorTokenBorderVar } from '../canvas/palette';
import { computeForeignKeyCardinality } from '../schema/mutations';
import type { TranslateFn } from '../i18n/types';

export interface HtmlExportInput {
  projectName: string;
  schema: SchemaModel;
  canvas: CanvasMetadata;
  /** Stejné hodnoty, jaké `CanvasView.tsx` právě má vykreslené (fallback pozice pro tabulky bez uloženého záznamu). */
  tablePositions: Record<string, Point>;
  tableSizes: Record<string, BoxSize>;
  t: TranslateFn;
  language: string;
  /** Injektovatelné jen kvůli deterministickým testům — jinak `new Date()`. */
  now?: Date;
}

const NOTE_WIDTH = 200;
const NOTE_MIN_HEIGHT = 96;
const NOTE_CHARS_PER_LINE = 26;
const NOTE_LINE_HEIGHT = 16;
const DIAGRAM_PADDING = 48;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Hrubý odhad výšky poznámkové bubliny podle délky textu — foreignObject je i tak `overflow: visible`. */
function estimateNoteBoxHeight(text: string): number {
  const lines = text.length === 0 ? [''] : text.split('\n');
  let wrapped = 0;
  for (const line of lines) wrapped += Math.max(1, Math.ceil(line.length / NOTE_CHARS_PER_LINE));
  return Math.max(NOTE_MIN_HEIGHT, 32 + wrapped * NOTE_LINE_HEIGHT);
}

interface NoteGroups {
  columnNotesById: Record<string, Note[]>;
  notesByTable: Record<string, Note[]>;
  notesByGroup: Record<string, Note[]>;
  freeNotes: Note[];
}

/** Stejné třídění podle `attachment.type`, jaké dělá `CanvasView.tsx`'s derive efekt (Epic 6/7). */
function groupNotes(notes: Note[]): NoteGroups {
  const columnNotesById: Record<string, Note[]> = {};
  const notesByTable: Record<string, Note[]> = {};
  const notesByGroup: Record<string, Note[]> = {};
  const freeNotes: Note[] = [];
  for (const note of notes) {
    if (note.attachment.type === 'column') (columnNotesById[note.attachment.id] ??= []).push(note);
    else if (note.attachment.type === 'table') (notesByTable[note.attachment.id] ??= []).push(note);
    else if (note.attachment.type === 'group') (notesByGroup[note.attachment.id] ??= []).push(note);
    else freeNotes.push(note);
  }
  return { columnNotesById, notesByTable, notesByGroup, freeNotes };
}

interface PlacedNote {
  note: Note;
  position: Point;
  height: number;
}

/** Stejný stacking algoritmus jako `CanvasView.tsx` (pinned poznámky nemají vlastní uloženou pozici). */
function placeNotes(
  input: HtmlExportInput,
  groups: NoteGroups,
  groupBoxById: Record<string, BoundingBox | null>,
): PlacedNote[] {
  const placed: PlacedNote[] = [];
  for (const [tableId, notes] of Object.entries(groups.notesByTable)) {
    const position = input.tablePositions[tableId];
    if (!position) continue;
    const size = input.tableSizes[tableId] ?? { width: TABLE_NODE_WIDTH, height: NOTE_STACK_OFFSET };
    notes.forEach((note, index) => {
      placed.push({
        note,
        position: { x: position.x + size.width + 24, y: position.y + index * NOTE_STACK_OFFSET },
        height: estimateNoteBoxHeight(note.text),
      });
    });
  }
  for (const [groupId, notes] of Object.entries(groups.notesByGroup)) {
    const box = groupBoxById[groupId];
    if (!box) continue;
    notes.forEach((note, index) => {
      placed.push({
        note,
        position: { x: box.x + box.width + 24, y: box.y + index * NOTE_STACK_OFFSET },
        height: estimateNoteBoxHeight(note.text),
      });
    });
  }
  for (const note of groups.freeNotes) {
    if (note.position) placed.push({ note, position: note.position, height: estimateNoteBoxHeight(note.text) });
  }
  return placed;
}

function renderTableForeignObject(
  table: TableModel,
  position: Point,
  size: BoxSize,
  color: ColorToken | null,
  viewSettings: ViewSettings,
  columnNotesById: Record<string, Note[]>,
  tableNoteCount: number,
  t: TranslateFn,
): string {
  const rows = visibleColumns(table, viewSettings);
  const styleAttr = color
    ? ` style="background:${colorTokenBackgroundVar(color)};border-color:${colorTokenBorderVar(color)}"`
    : '';
  const rowsHtml = rows
    .map((column) => {
      const isPk = table.primaryKey?.columns.includes(column.name) ?? false;
      const noteCount = columnNotesById[column.id]?.length ?? 0;
      const nameClass = isPk ? 'table-node__column-name table-node__column-name--pk' : 'table-node__column-name';
      const parts = [`<span class="${nameClass}">${escapeHtml(column.name)}</span>`];
      if (viewSettings.showColumnType) {
        parts.push(`<span class="table-node__column-type">${escapeHtml(column.canonicalType ?? column.rawType)}</span>`);
      }
      if (viewSettings.showNullable) {
        parts.push(
          `<span class="table-node__column-nullable">${escapeHtml(
            t(column.nullable ? 'canvas.column.nullable' : 'canvas.column.notNullable'),
          )}</span>`,
        );
      }
      if (viewSettings.showDefault && column.defaultValue !== null) {
        parts.push(
          `<span class="table-node__column-default" title="${escapeHtml(column.defaultValue)}">= ${escapeHtml(column.defaultValue)}</span>`,
        );
      }
      if (viewSettings.showComment && column.comment !== null) {
        parts.push(`<span class="table-node__column-comment" title="${escapeHtml(column.comment)}">i</span>`);
      }
      if (noteCount > 0) parts.push(`<span class="note-marker note-marker--column">${noteCount}</span>`);
      return `<div class="table-node__column">${parts.join('')}</div>`;
    })
    .join('');

  return `<a href="#table-doc-${table.id}" class="export-diagram__table-link" aria-label="${escapeHtml(table.name)}">
    <foreignObject x="${position.x}" y="${position.y}" width="${size.width}" height="${size.height}" style="overflow:visible">
      <div xmlns="http://www.w3.org/1999/xhtml" class="table-node"${styleAttr}>
        <div class="table-node__header">
          <span class="table-node__title">${escapeHtml(table.name)}</span>
          ${tableNoteCount > 0 ? `<span class="note-marker">${tableNoteCount}</span>` : ''}
        </div>
        <div class="table-node__columns">${rowsHtml}</div>
      </div>
    </foreignObject>
  </a>`;
}

function renderGroupForeignObject(group: Group, box: BoundingBox): string {
  return `<foreignObject x="${box.x}" y="${box.y}" width="${box.width}" height="${box.height}" style="overflow:visible">
    <div xmlns="http://www.w3.org/1999/xhtml" class="group-frame" style="width:${box.width}px;height:${box.height}px">
      <span class="group-frame__label">${escapeHtml(group.name)}</span>
    </div>
  </foreignObject>`;
}

function renderNoteForeignObject(placed: PlacedNote): string {
  const html = escapeHtml(placed.note.text).replace(/\n/g, '<br/>');
  return `<foreignObject x="${placed.position.x}" y="${placed.position.y}" width="${NOTE_WIDTH}" height="${placed.height}" style="overflow:visible">
    <div xmlns="http://www.w3.org/1999/xhtml" class="note-bubble">
      <div class="note-bubble__text">${html}</div>
    </div>
  </foreignObject>`;
}

function renderEdgeLabel(text: string, point: Point, dx: number): string {
  return `<foreignObject x="${point.x + dx - 14}" y="${point.y - 9}" width="28" height="18" style="overflow:visible">
    <div xmlns="http://www.w3.org/1999/xhtml" class="export-edge-label">${escapeHtml(text)}</div>
  </foreignObject>`;
}

function renderEdgeSvg(
  schema: SchemaModel,
  canvas: CanvasMetadata,
  tablePositions: Record<string, Point>,
  tableSizes: Record<string, BoxSize>,
): string {
  return resolveForeignKeyEdges(schema)
    .flatMap((resolved) => {
      if (!resolved.targetTableId) return [];
      const sourceTable = schema.tables.find((table) => table.id === resolved.sourceTableId);
      const targetTable = schema.tables.find((table) => table.id === resolved.targetTableId);
      if (!sourceTable || !targetTable) return [];
      const sourcePosition = tablePositions[sourceTable.id];
      const targetPosition = tablePositions[targetTable.id];
      if (!sourcePosition || !targetPosition) return [];
      const sourceSize = tableSizes[sourceTable.id] ?? { width: TABLE_NODE_WIDTH, height: 90 };
      const targetSize = tableSizes[targetTable.id] ?? { width: TABLE_NODE_WIDTH, height: 90 };
      const sourceColumn = sourceTable.columns.find((column) => column.name === resolved.foreignKey.columns[0]);
      const targetColumn = targetTable.columns.find((column) => column.name === resolved.foreignKey.referencedColumns[0]);
      const sourceRowOffset = sourceColumn ? columnRowCenterOffset(sourceTable, sourceColumn.id, canvas.viewSettings) : null;
      const targetRowOffset = targetColumn ? columnRowCenterOffset(targetTable, targetColumn.id, canvas.viewSettings) : null;
      const route = getEdgeRoute(canvas, resolved.foreignKey.id);
      const sourceEndpoint: EdgeEndpoint = { positionAbsolute: sourcePosition, size: sourceSize, rowOffset: sourceRowOffset };
      const targetEndpoint: EdgeEndpoint = { positionAbsolute: targetPosition, size: targetSize, rowOffset: targetRowOffset };
      const visual = computeEdgeVisual(sourceEndpoint, targetEndpoint, route.waypoints, sourceTable.id === targetTable.id);
      const cardinality = computeForeignKeyCardinality(sourceTable, resolved.foreignKey);

      const labels = canvas.viewSettings.showCardinalityLabel
        ? renderEdgeLabel(cardinality === 'one' ? '1' : 'N', visual.sourceLabelPoint, visual.sourceLabelDx) +
          renderEdgeLabel('1', visual.targetLabelPoint, visual.targetLabelDx)
        : '';
      return [
        `<g class="export-edge"><path d="${visual.path}" style="fill:none;stroke:var(--color-text-muted);stroke-width:1.5" />${labels}</g>`,
      ];
    })
    .join('');
}

function computeDiagramBoundingBox(
  schema: SchemaModel,
  tablePositions: Record<string, Point>,
  tableSizes: Record<string, BoxSize>,
  groupBoxes: BoundingBox[],
  placedNotes: PlacedNote[],
): BoundingBox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const extend = (x: number, y: number) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };
  for (const table of schema.tables) {
    const position = tablePositions[table.id];
    if (!position) continue;
    const size = tableSizes[table.id] ?? { width: TABLE_NODE_WIDTH, height: 90 };
    extend(position.x, position.y);
    extend(position.x + size.width, position.y + size.height);
  }
  for (const box of groupBoxes) {
    extend(box.x, box.y);
    extend(box.x + box.width, box.y + box.height);
  }
  for (const placed of placedNotes) {
    extend(placed.position.x, placed.position.y);
    extend(placed.position.x + NOTE_WIDTH, placed.position.y + placed.height);
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, width: TABLE_NODE_WIDTH, height: 200 };
  return {
    x: minX - DIAGRAM_PADDING,
    y: minY - DIAGRAM_PADDING,
    width: maxX - minX + DIAGRAM_PADDING * 2,
    height: maxY - minY + DIAGRAM_PADDING * 2,
  };
}

function renderDiagramSvg(input: HtmlExportInput): string {
  const { schema, canvas, tablePositions, tableSizes } = input;
  const notes = groupNotes(canvas.notes);
  const groupBoxById: Record<string, BoundingBox | null> = {};
  for (const group of canvas.groups) {
    groupBoxById[group.id] = computeGroupBoundingBox(group.memberTableIds, tablePositions, tableSizes);
  }
  const groupBoxes = Object.values(groupBoxById).filter((box): box is BoundingBox => box !== null);
  const placedNotes = placeNotes(input, notes, groupBoxById);
  const viewBox = computeDiagramBoundingBox(schema, tablePositions, tableSizes, groupBoxes, placedNotes);

  const edgesSvg = renderEdgeSvg(schema, canvas, tablePositions, tableSizes);
  const groupsSvg = canvas.groups
    .map((group) => {
      const box = groupBoxById[group.id];
      return box ? renderGroupForeignObject(group, box) : '';
    })
    .join('');
  const tablesSvg = schema.tables
    .map((table) => {
      const position = tablePositions[table.id];
      if (!position) return '';
      const size = tableSizes[table.id] ?? { width: TABLE_NODE_WIDTH, height: 90 };
      return renderTableForeignObject(
        table,
        position,
        size,
        canvas.tableColors[table.id] ?? null,
        canvas.viewSettings,
        notes.columnNotesById,
        notes.notesByTable[table.id]?.length ?? 0,
        input.t,
      );
    })
    .join('');
  const notesSvg = placedNotes.map(renderNoteForeignObject).join('');

  return `<svg class="export-diagram__svg" width="${viewBox.width}" height="${viewBox.height}" viewBox="${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}" xmlns="http://www.w3.org/2000/svg">
    <g class="export-diagram__edges">${edgesSvg}</g>
    <g class="export-diagram__groups">${groupsSvg}</g>
    <g class="export-diagram__tables">${tablesSvg}</g>
    <g class="export-diagram__notes">${notesSvg}</g>
  </svg>`;
}

function keyLabel(table: TableModel, column: ColumnModel, t: TranslateFn): string {
  const isPk = table.primaryKey?.columns.includes(column.name) ?? false;
  const isFk = table.foreignKeys.some((fk) => fk.columns.includes(column.name));
  if (isPk && isFk) return t('export.column.key.both');
  if (isPk) return t('export.column.key.primary');
  if (isFk) return t('export.column.key.foreign');
  return t('inspector.table.none');
}

function renderNoteListItem(text: string): string {
  return `<li>${escapeHtml(text).replace(/\n/g, '<br/>')}</li>`;
}

function renderTableDocumentation(
  table: TableModel,
  columnNotesById: Record<string, Note[]>,
  tableNotes: Note[],
  groupNotesEntries: Array<{ groupName: string; notes: Note[] }>,
  t: TranslateFn,
): string {
  const sortedColumns = [...table.columns].sort((a, b) => a.ordinal - b.ordinal);

  const columnRows = sortedColumns
    .map((column) => {
      const notes = columnNotesById[column.id] ?? [];
      return `<tr>
        <td>${escapeHtml(column.name)}</td>
        <td>${escapeHtml(column.canonicalType ?? column.rawType)}</td>
        <td>${escapeHtml(t(column.nullable ? 'canvas.column.nullable' : 'canvas.column.notNullable'))}</td>
        <td>${column.defaultValue !== null ? escapeHtml(column.defaultValue) : '—'}</td>
        <td>${keyLabel(table, column, t)}</td>
        <td class="export-doc__db-comment">${column.comment !== null ? escapeHtml(column.comment) : '—'}</td>
        <td class="export-doc__note-cell">${
          notes.length > 0 ? `<ul class="export-doc__note-list">${notes.map((note) => renderNoteListItem(note.text)).join('')}</ul>` : '—'
        }</td>
      </tr>`;
    })
    .join('');

  const indexesHtml =
    table.indexes.length === 0
      ? `<p class="hint">${t('inspector.table.none')}</p>`
      : `<ul class="export-doc__list">${table.indexes
          .map(
            (index) =>
              `<li><strong>${escapeHtml(index.name)}</strong>${index.isUnique ? ` (${escapeHtml(t('export.index.unique'))})` : ''} — ${
                index.columns.length > 0 ? escapeHtml(index.columns.join(', ')) : escapeHtml(index.definition ?? '')
              }</li>`,
          )
          .join('')}</ul>`;

  const uniqueHtml =
    table.uniqueConstraints.length === 0
      ? `<p class="hint">${t('inspector.table.none')}</p>`
      : `<ul class="export-doc__list">${table.uniqueConstraints
          .map((unique) => `<li><strong>${escapeHtml(unique.name)}</strong> — ${escapeHtml(unique.columns.join(', '))}</li>`)
          .join('')}</ul>`;

  const checkHtml =
    table.checkConstraints.length === 0
      ? `<p class="hint">${t('inspector.table.none')}</p>`
      : `<ul class="export-doc__list">${table.checkConstraints
          .map((check) => `<li><strong>${escapeHtml(check.name)}</strong> — ${escapeHtml(check.expression)}</li>`)
          .join('')}</ul>`;

  const foreignKeysHtml =
    table.foreignKeys.length === 0
      ? `<p class="hint">${t('inspector.table.none')}</p>`
      : `<ul class="export-doc__list">${table.foreignKeys
          .map(
            (fk) =>
              `<li><strong>${escapeHtml(fk.name)}</strong> — ${escapeHtml(fk.columns.join(', '))} → ${escapeHtml(
                `${fk.referencedSchema}.${fk.referencedTable}`,
              )}(${escapeHtml(fk.referencedColumns.join(', '))}), ON DELETE ${fk.onDelete}, ON UPDATE ${fk.onUpdate}</li>`,
          )
          .join('')}</ul>`;

  const hasNotes = tableNotes.length > 0 || groupNotesEntries.some((entry) => entry.notes.length > 0);
  const notesHtml = hasNotes
    ? `<div class="export-doc__notes">
        <ul class="export-doc__note-list">
          ${tableNotes.map((note) => renderNoteListItem(note.text)).join('')}
          ${groupNotesEntries
            .flatMap((entry) =>
              entry.notes.map((note) => `<li>${escapeHtml(t('export.notes.fromGroup', { name: entry.groupName }))}: ${escapeHtml(note.text)}</li>`),
            )
            .join('')}
        </ul>
      </div>`
    : `<p class="hint">${t('inspector.notes.empty')}</p>`;

  return `<section id="table-doc-${table.id}" class="export-doc__table">
    <h3>${escapeHtml(table.name)}</h3>
    <p class="hint">${t('inspector.table.schema')}: ${escapeHtml(table.schema)}</p>

    <h4>${t('inspector.section.comment')}</h4>
    <p class="export-doc__db-comment">${table.comment !== null ? escapeHtml(table.comment) : t('inspector.comment.empty')}</p>

    <div class="export-doc__table-scroll">
      <table>
        <thead>
          <tr>
            <th>${t('export.column.header.name')}</th>
            <th>${t('export.column.header.type')}</th>
            <th>${t('export.column.header.nullable')}</th>
            <th>${t('export.column.header.default')}</th>
            <th>${t('export.column.header.key')}</th>
            <th>${t('export.column.header.comment')}</th>
            <th>${t('export.column.header.notes')}</th>
          </tr>
        </thead>
        <tbody>${columnRows}</tbody>
      </table>
    </div>

    <h4>${t('inspector.table.primaryKey')}</h4>
    <p class="hint">${table.primaryKey ? escapeHtml(table.primaryKey.columns.join(', ')) : t('inspector.table.none')}</p>

    <h4>${t('inspector.table.foreignKeys')}</h4>
    ${foreignKeysHtml}

    <h4>${t('inspector.table.uniqueConstraints')}</h4>
    ${uniqueHtml}

    <h4>${t('inspector.table.checkConstraints')}</h4>
    ${checkHtml}

    <h4>${t('inspector.table.indexes')}</h4>
    ${indexesHtml}

    <h4>${t('inspector.section.notes')}</h4>
    ${notesHtml}
  </section>`;
}

function renderDocumentation(input: HtmlExportInput): string {
  const { schema, canvas, t } = input;
  const notes = groupNotes(canvas.notes);
  const sortedTables = [...schema.tables].sort((a, b) => a.name.localeCompare(b.name));

  const tablesHtml = sortedTables
    .map((table) => {
      const groupId = canvas.groups.find((group) => group.memberTableIds.includes(table.id))?.id;
      const groupNotesEntries = groupId
        ? [{ groupName: canvas.groups.find((group) => group.id === groupId)!.name, notes: notes.notesByGroup[groupId] ?? [] }]
        : [];
      return renderTableDocumentation(
        table,
        notes.columnNotesById,
        notes.notesByTable[table.id] ?? [],
        groupNotesEntries,
        t,
      );
    })
    .join('');

  const freeNotesHtml =
    notes.freeNotes.length === 0
      ? ''
      : `<section class="export-doc__free-notes">
          <h2 class="export-section-title">${t('export.freeNotes.title')}</h2>
          <ul class="export-doc__note-list">${notes.freeNotes.map((note) => renderNoteListItem(note.text)).join('')}</ul>
        </section>`;

  return `<section class="export-doc">
    <h2 class="export-section-title">${t('export.documentation.title')}</h2>
    ${tablesHtml}
  </section>
  ${freeNotesHtml}`;
}

const EXPORT_STYLE = `
:root {
  --color-bg: #f6f7f9;
  --color-surface: #ffffff;
  --color-surface-alt: #f0f2f5;
  --color-border: #d8dce3;
  --color-text: #1c2430;
  --color-text-muted: #5b6674;
  --color-warning-bg: #fdf3d8;
  --color-warning-border: #e0c179;
  --radius: 8px;

  --canvas-color-slate-bg: #eceff3; --canvas-color-slate-border: #94a0b2;
  --canvas-color-red-bg: #fbe9e7; --canvas-color-red-border: #d97567;
  --canvas-color-orange-bg: #fdecdc; --canvas-color-orange-border: #e0994f;
  --canvas-color-amber-bg: #fdf3d3; --canvas-color-amber-border: #cba238;
  --canvas-color-green-bg: #e4f3e6; --canvas-color-green-border: #5da16a;
  --canvas-color-teal-bg: #dff3f0; --canvas-color-teal-border: #3f9c8d;
  --canvas-color-blue-bg: #e3edfd; --canvas-color-blue-border: #5285d6;
  --canvas-color-indigo-bg: #e9e8fb; --canvas-color-indigo-border: #6d68c9;
  --canvas-color-violet-bg: #f1e6fa; --canvas-color-violet-border: #9968c2;
  --canvas-color-pink-bg: #fbe6f0; --canvas-color-pink-border: #cf6199;
  color-scheme: light;
}
@media (prefers-color-scheme: dark) {
  :root {
    --color-bg: #14181d; --color-surface: #1c222a; --color-surface-alt: #232b35;
    --color-border: #343d49; --color-text: #e7ecf2; --color-text-muted: #a1adbb;
    --color-warning-bg: #3a3220; --color-warning-border: #7a6733;

    --canvas-color-slate-bg: #2a323d; --canvas-color-slate-border: #8894a6;
    --canvas-color-red-bg: #3a2624; --canvas-color-red-border: #e08b7c;
    --canvas-color-orange-bg: #3a2c1a; --canvas-color-orange-border: #e3a561;
    --canvas-color-amber-bg: #392f16; --canvas-color-amber-border: #d9b653;
    --canvas-color-green-bg: #1f3324; --canvas-color-green-border: #79bd86;
    --canvas-color-teal-bg: #17332f; --canvas-color-teal-border: #56b8a7;
    --canvas-color-blue-bg: #1e2c40; --canvas-color-blue-border: #7ba1e0;
    --canvas-color-indigo-bg: #262441; --canvas-color-indigo-border: #8b85dd;
    --canvas-color-violet-bg: #2d2338; --canvas-color-violet-border: #b087d9;
    --canvas-color-pink-bg: #391f2c; --canvas-color-pink-border: #de85af;
    color-scheme: dark;
  }
}
* { box-sizing: border-box; }
body {
  margin: 0;
  background: var(--color-bg);
  color: var(--color-text);
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  scroll-behavior: smooth;
}
h1, h2, h3, h4 { margin: 0 0 12px; line-height: 1.25; }
h2.export-section-title { margin-top: 40px; }
h3 { margin-top: 32px; padding-top: 16px; border-top: 1px solid var(--color-border); }
h4 { font-size: 13px; margin-top: 20px; }
p { margin: 0 0 12px; }
.hint { color: var(--color-text-muted); font-size: 12px; }
table { width: 100%; border-collapse: collapse; }
th, td { text-align: left; padding: 6px 10px; border-bottom: 1px solid var(--color-border); vertical-align: top; }
th { font-size: 11px; text-transform: uppercase; letter-spacing: 0.03em; color: var(--color-text-muted); }
.export-header {
  padding: 20px 24px;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
}
.export-header h1 { margin-bottom: 4px; }
.export-main { max-width: 1100px; margin: 0 auto; padding: 24px; }
/* Diagram se záměrně vymaní z max-width sloupce dokumentace (stejný trik jako
   .canvas-page-break v editoru) — schéma dostane celou šířku okna, aby se
   u větších projektů minimalizovalo horizontální scrollování. */
.export-diagram {
  margin-bottom: 32px;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  padding: 0 24px;
  box-sizing: border-box;
}
.export-diagram__scroll {
  overflow: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
}
.export-diagram__table-link { cursor: pointer; }
.export-edge-label {
  display: flex; align-items: center; justify-content: center;
  width: 100%; height: 100%; box-sizing: border-box;
  border-radius: 999px; background: var(--color-surface); border: 1px solid var(--color-border);
  color: var(--color-text-muted); font-size: 10px;
}
.table-node {
  width: 100%; height: 100%;
  border: 1.5px solid var(--color-border); border-radius: var(--radius);
  background: var(--color-surface); font-size: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.table-node__header {
  display: flex; align-items: center; gap: 6px; height: 36px; padding: 0 8px;
  border-bottom: 1px solid var(--color-border); font-weight: 600;
}
.table-node__title { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.table-node__columns { display: flex; flex-direction: column; }
.table-node__column {
  position: relative; display: flex; align-items: center; gap: 6px; height: 26px;
  padding: 0 8px; border-bottom: 1px solid var(--color-border);
}
.table-node__column:last-child { border-bottom: none; }
.table-node__column-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.table-node__column-name--pk { font-weight: 600; }
.table-node__column-type { color: var(--color-text-muted); font-size: 11px; white-space: nowrap; }
.table-node__column-nullable { color: var(--color-text-muted); font-size: 10px; white-space: nowrap; flex: none; }
.table-node__column-default {
  color: var(--color-text-muted); font-size: 11px; white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; max-width: 60px; flex: none;
}
.table-node__column-comment {
  display: inline-flex; align-items: center; justify-content: center; width: 14px; height: 14px;
  border-radius: 50%; background: var(--color-surface-alt); border: 1px solid var(--color-border);
  color: var(--color-text-muted); font-size: 9px; font-style: italic; font-weight: 700; flex: none;
}
.note-marker {
  display: inline-flex; align-items: center; justify-content: center; min-width: 15px; height: 15px;
  padding: 0 3px; border-radius: 999px; background: var(--color-warning-bg);
  border: 1px solid var(--color-warning-border); font-size: 10px; line-height: 1; flex: none;
}
.group-frame {
  border: 2px dashed var(--color-text-muted); border-radius: var(--radius);
  background: rgba(127, 127, 127, 0.04);
}
.group-frame__label {
  position: relative; top: -14px; left: 8px; padding: 1px 8px; font-size: 12px; font-weight: 600;
  background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 999px;
}
.note-bubble {
  width: 100%; height: 100%; border: 1px solid var(--color-warning-border); border-radius: var(--radius);
  background: var(--color-warning-bg); box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12); overflow: hidden;
}
.note-bubble__text { padding: 8px 10px; font-size: 12px; white-space: pre-wrap; overflow-wrap: break-word; }
.export-doc__table { padding-bottom: 8px; }
.export-doc__db-comment { font-style: italic; }
.export-doc__notes, .export-doc__note-cell ul, .export-doc__free-notes ul {
  background: var(--color-warning-bg); border: 1px solid var(--color-warning-border);
  border-radius: var(--radius); padding: 8px 10px 8px 24px; margin: 0;
}
.export-doc__note-cell { padding: 4px 6px; }
.export-doc__list { margin: 0; padding-left: 20px; }
.export-doc__table-scroll { overflow-x: auto; }
.export-doc__free-notes { margin-top: 32px; padding-top: 16px; border-top: 1px solid var(--color-border); }
`;

/**
 * Sestaví hotový self-contained HTML dokument (E12-US1). Nevolá žádné API,
 * nezapisuje do historie ani jinak nemění projekt — čistá funkce nad daty,
 * která volající (`ExportPanel.tsx`) už má v paměti z běžícího editoru.
 */
export function buildExportHtml(input: HtmlExportInput): string {
  const { projectName, t, language } = input;
  const now = input.now ?? new Date();
  const generatedAt = new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(now);
  const title = t('export.documentTitle', { name: projectName });

  return `<!doctype html>
<html lang="${escapeHtml(language)}">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>${EXPORT_STYLE}</style>
</head>
<body>
  <header class="export-header">
    <h1>${escapeHtml(projectName)}</h1>
    <p class="hint">${escapeHtml(t('export.generatedAt', { time: generatedAt }))}</p>
  </header>
  <main class="export-main">
    <section class="export-diagram">
      <h2 class="export-section-title">${t('export.diagram.title')}</h2>
      <div class="export-diagram__scroll">${renderDiagramSvg(input)}</div>
    </section>
    ${renderDocumentation(input)}
  </main>
</body>
</html>`;
}
